import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import { ApiResponse, NavbarMenu, UserAlert } from "@/types/global";
import type {
  NavbarGroupRoleCreate,
  NavbarRoleCreate,
  NavbarViewConfigCreate,
  NavbarViewConfigInputs,
} from "@/types/userManagement";

export default class AppSettingsApi extends ApiVerified {
  // CONFLICT-N5 (usermgmt-3-5): dropped the `affiliationId` param — RoleId is read from the JWT
  // server-side (BR-UI-901). Consumer appSettingsStore.getNavbarMenu() already calls this no-arg.
  async getNavbarMenu(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<NavbarMenu>(`app-settings/get-navbar-menu`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ── User Management v3 (pvni-user-mgmt-v3) — navbar configuration (SP6, ADMIN-only) ──
  // Entry order ViewConfigs → GroupRoles → Roles (BR-9). 403 = not ADMIN · 417 = unknown ref ·
  // 500 = seed-data missing; messages surface verbatim via handleApiError into navbarConfigStore.FormError.

  // POST /v7/app-settings/save-navbar-view-config → true (api-binding §11). Body is the editable
  // NavbarViewConfigInputs — the server stamps CreatedBy/CreatedOn, so they are not sent.
  async saveNavbarViewConfig(config: NavbarViewConfigInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `app-settings/save-navbar-view-config`,
        config,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // GET /v7/app-settings/get-navbar-view-config?viewName= → NavbarViewConfigCreate (api-binding §12).
  async getNavbarViewConfig(viewName: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<NavbarViewConfigCreate>(
        `app-settings/get-navbar-view-config`,
        { params: { viewName: viewName } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // GET /v7/app-settings/get-navbar-view-configs → Array<NavbarViewConfigCreate>. Every saved
  // record in one call; names with no record are simply absent from the list.
  async getNavbarViewConfigs(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<NavbarViewConfigCreate>>(
        `app-settings/get-navbar-view-configs`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // POST /v7/app-settings/save-navbar-group-role → true (api-binding §13).
  async saveNavbarGroupRole(config: NavbarGroupRoleCreate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `app-settings/save-navbar-group-role`,
        config,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // GET /v7/app-settings/get-navbar-group-role?groupRoleId= → NavbarGroupRoleCreate (api-binding §14).
  async getNavbarGroupRole(groupRoleId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<NavbarGroupRoleCreate>(
        `app-settings/get-navbar-group-role`,
        { params: { groupRoleId: groupRoleId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // POST /v7/app-settings/save-navbar-role → true (api-binding §15).
  async saveNavbarRole(config: NavbarRoleCreate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `app-settings/save-navbar-role`,
        config,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // GET /v7/app-settings/get-navbar-role?roleId= → NavbarRoleCreate (api-binding §16).
  async getNavbarRole(roleId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<NavbarRoleCreate>(
        `app-settings/get-navbar-role`,
        { params: { roleId: roleId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getUserAlerts(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<UserAlert>>(
        `app-settings/get-user-alerts`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
